import {
  knowYourRightsMenu,
  productsMenu,
  companyMenu,
  supportMenu,
  legalMenu,
  topMenu,
  imprintMenu,
} from "config/menus";
import { useServerSideStoryblokPages } from "contexts/DataContext/hooks";
import type { LanguageCode } from "config/languages";
import type { MenuExtendedProps } from "config/menus/menus.d";
import type { ServerSideStoryblokPages } from "types/storyblok";
import getDomainFromLanguage from "utils/getDomainFromLanguage";
import { getPageUrl } from "utils/getPageUrl";
import { getParentLanguage } from "utils/localeUtils";
import type { ResolvedMenuPagePaths } from "./menuUtils.d";

const DEFAULT_FALLBACK_LANGUAGE: LanguageCode = "en";
const NON_OVERBOOKING_PAGE_IN_MENU_LANGUAGES: LanguageCode[] = [
  "pt-pt",
  "pt-br",
  "ro",
  "ro-md",
  "sv",
  "fi",
  "nb",
  "ru",
  "el",
];

const getStoryblokMenuHrefWithDomain = (language: LanguageCode, href: string) =>
  href && `${getDomainFromLanguage(language)}${href}`;

export const getStoryblokMenuHref = (
  storyblokId: number,
  serverSideStoryblokPages: ServerSideStoryblokPages,
  language: LanguageCode,
  parentLanguage: LanguageCode,
  fallback: LanguageCode
) =>
  getStoryblokMenuHrefWithDomain(language, serverSideStoryblokPages[storyblokId][language]) ||
  getStoryblokMenuHrefWithDomain(parentLanguage, serverSideStoryblokPages[storyblokId][parentLanguage]) ||
  getStoryblokMenuHrefWithDomain(fallback, serverSideStoryblokPages[storyblokId][fallback]) ||
  getStoryblokMenuHrefWithDomain(
    DEFAULT_FALLBACK_LANGUAGE,
    serverSideStoryblokPages[storyblokId][DEFAULT_FALLBACK_LANGUAGE]
  ) ||
  "";

const resolveMenuPagePaths = (language: LanguageCode, menu: MenuExtendedProps[]): ResolvedMenuPagePaths[] => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const serverSideStoryblokPages = useServerSideStoryblokPages();
  const parentLanguage = getParentLanguage(language);
  return menu.map((item) => {
    const { storyblokId, fallback, page, subMenu } = item;

    if (storyblokId) {
      const href = getStoryblokMenuHref(storyblokId, serverSideStoryblokPages, language, parentLanguage, fallback);
      return {
        href: href.endsWith("/") ? href : `${href}/`,
        ...item,
      };
    }

    if (Object.prototype.hasOwnProperty.call(item, "subMenu")) {
      return {
        ...item,
        subMenu: resolveMenuPagePaths(language, subMenu),
      };
    }

    return {
      href: getPageUrl(language, page),
      ...item,
    };
  });
};

export const useKnowYourRightsMenu = (language: LanguageCode) => {
  let menu = knowYourRightsMenu;
  menu = NON_OVERBOOKING_PAGE_IN_MENU_LANGUAGES.includes(language)
    ? menu.filter((item) => item.storyblokId !== 71636186)
    : menu;
  menu = ["el", "ru"].includes(language) ? menu.filter((item) => item.page !== "airports") : menu;

  return resolveMenuPagePaths(language, menu);
};

export const useProductsMenu = (language: LanguageCode) => resolveMenuPagePaths(language, productsMenu);

export const useCompanyMenu = (language: LanguageCode) => resolveMenuPagePaths(language, companyMenu);

export const useSupportMenu = (language: LanguageCode) => {
  const ourFeesId = 142848538;
  const priceListId = 52702007;
  const notTranslatedOurFeesPages: LanguageCode[] = ["pt-br", "ro-md"];

  let menu = supportMenu;

  menu = notTranslatedOurFeesPages.includes(language)
    ? menu.filter((item) => item.storyblokId !== ourFeesId)
    : menu.filter((item) => item.storyblokId !== priceListId);

  return resolveMenuPagePaths(language, menu);
};

export const useLegalMenu = (language: LanguageCode) => resolveMenuPagePaths(language, legalMenu);

export const useTopMenu = (language: LanguageCode) => {
  const menu = topMenu;
  const knowYourRightsMenuMapped = useKnowYourRightsMenu(language);

  menu.map((item) => {
    const menuItem = item;
    if (menuItem.subMenu) menuItem.subMenu = knowYourRightsMenuMapped;

    return menuItem;
  });

  return resolveMenuPagePaths(language, menu);
};

export const useImprintMenu = (language: LanguageCode) => {
  let menu = imprintMenu;
  menu = ["en", "de"].includes(getParentLanguage(language))
    ? menu
    : menu.filter((item) => item.storyblokId !== 52883090);

  return resolveMenuPagePaths(language, menu);
};
